import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export interface SyncStatusResponse {
  success: boolean;
  summary: {
    total: number;
    inSync: number;
    outOfSync: number;
    errors: number;
  };
  outOfSync: Array<{
    profileId: number;
    databasePrice: string;
    blockchainPrice: string;
    difference: string;
  }>;
  inSync: Array<{
    profileId: number;
    price: string;
  }>;
  errors: Array<{
    profileId: number;
    message: string;
  }>;
}

export interface SyncSingleProfileResponse {
  success: boolean;
  profileId: number;
  before: {
    price: string;
  };
  after: {
    price: string;
  };
  mismatch: boolean;
  message: string;
}

export interface SyncAllProfilesResponse {
  success: boolean;
  results: {
    total: number;
    synced: number;
    mismatches: number;
    errors: number;
    details: Array<{
      profileId: number;
      status: 'updated' | 'in_sync' | 'error';
      oldPrice?: string;
      newPrice?: string;
      message?: string;
    }>;
  };
}

/**
 * Get sync status - shows which profiles are out of sync
 */
export const getSyncStatus = createAsyncThunk<SyncStatusResponse>(
  'admin/getSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<SyncStatusResponse>('/admin/sync/status');
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to get sync status';
      return rejectWithValue(message);
    }
  }
);

/**
 * Sync a single profile from blockchain to database
 */
export const syncSingleProfile = createAsyncThunk<SyncSingleProfileResponse, number>(
  'admin/syncSingleProfile',
  async (profileId, { rejectWithValue }) => {
    try {
      const response = await api.post<SyncSingleProfileResponse>(`/admin/sync/profile/${profileId}`);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to sync profile';
      return rejectWithValue(message);
    }
  }
);

/**
 * Sync all profiles from blockchain to database
 */
export const syncAllProfiles = createAsyncThunk<SyncAllProfilesResponse>(
  'admin/syncAllProfiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post<SyncAllProfilesResponse>('/admin/sync/all');
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to sync all profiles';
      return rejectWithValue(message);
    }
  }
);
